import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Image from '../AppImage';
import { useAuth } from '../../contexts/AuthContext';

const UserAccountMenu = () => {
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const defaultUser = {
    name: 'John Doe',
    email: 'john.doe@appcreator.com',
    avatar: null,
    role: 'Creator'
  };

  const currentUser = user || defaultUser;

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleMenuItemClick = (action) => {
    // Execute action immediately
    if (action) {
      action();
    }
    // Close dropdown after a tiny delay to ensure action completes
    setTimeout(() => {
      setIsOpen(false);
    }, 50);
  };

  const menuItems = [
    {
      label: 'Account Settings',
      icon: 'Settings',
      action: () => navigate('/project-settings')
    },
    {
      label: 'Workspace',
      icon: 'Users',
      action: () => console.log('Workspace clicked')
    },
    {
      label: 'Help & Support',
      icon: 'HelpCircle',
      action: () => console.log('Help & Support clicked')
    },
    {
      label: 'Logout',
      icon: 'LogOut',
      action: handleSignOut,
      variant: 'destructive'
    }
  ];

  useEffect(() => {
    if (!isOpen) return;

    // Add a small delay before attaching the listener to prevent immediate closure
    const timeoutId = setTimeout(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
          setIsOpen(false);
        }
      };

      const handleEscape = (event) => {
        if (event?.key === 'Escape') {
          setIsOpen(false);
        }
      };

      document.addEventListener('click', handleClickOutside, true);
      document.addEventListener('keydown', handleEscape);

      return () => {
        document.removeEventListener('click', handleClickOutside, true);
        document.removeEventListener('keydown', handleEscape);
      };
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [isOpen]);

  const getInitials = (name) => {
    return name?.split(' ')?.map(word => word?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
  };

  return (
    <div className="relative px-4 py-4 border-t border-border mt-auto" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-all duration-250 group"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {currentUser?.avatar ? (
          <Image
            src={currentUser?.avatar}
            alt={currentUser?.name}
            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-heading font-semibold text-sm flex-shrink-0">
            {getInitials(currentUser?.name)}
          </div>
        )}
        <div className="flex-1 text-left min-w-0">
          <div className="caption font-medium text-foreground truncate">
            {currentUser?.name}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {currentUser?.email}
          </div>
        </div>
        <Icon
          name="MoreVertical"
          size={16}
          className="text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0"
        />
      </button>
      {isOpen && (
        <div 
          className="absolute bottom-full left-4 right-4 mb-2 bg-popover border border-border rounded-lg shadow-lg overflow-hidden z-50"
          style={{ pointerEvents: 'auto' }}
        >
          <div className="p-2 border-b border-border">
            <div className="px-3 py-2">
              <div className="caption font-medium text-foreground">
                {currentUser?.name}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {currentUser?.role}
              </div>
            </div>
          </div>
          <div className="p-2">
            {menuItems?.map((item, index) => (
              <button
                key={index}
                type="button"
                onMouseDown={(e) => {
                  e?.preventDefault();
                  e?.stopPropagation();
                  handleMenuItemClick(item?.action);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-250 caption font-medium cursor-pointer ${
                  item?.variant === 'destructive' ?'text-destructive hover:bg-destructive/10' :'text-foreground hover:bg-muted'
                }`}
                style={{ pointerEvents: 'auto' }}
              >
                <Icon
                  name={item?.icon}
                  size={16}
                  className={item?.variant === 'destructive' ? 'text-destructive' : 'text-muted-foreground'}
                />
                <span>{item?.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAccountMenu;